(() => {
  if (window.AecisInstantProductSections) {
    window.AecisInstantProductSections.init(document);
    return;
  }

  const instances = new Map();
  const moneyFormat = () => window.theme_variables?.settings?.money_with_currency_format || '';
  const money = (cents) => {
    const format = moneyFormat();
    try {
      if (window.SHTHelper && typeof window.SHTHelper.formatMoney === 'function') {
        return window.SHTHelper.formatMoney(cents, format);
      }
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, format);
      }
    } catch (_) {
      // Keep the dependency-free fallback below available during partial theme loads.
    }
    const amount = Number(cents) / 100;
    if (!Number.isFinite(amount)) return '';
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  };

  const rootsIn = (scope, selector) => {
    const roots = [];
    if (scope?.matches?.(selector)) roots.push(scope);
    if (scope?.querySelectorAll) roots.push(...scope.querySelectorAll(selector));
    return [...new Set(roots)];
  };

  function initTabs(scope) {
    rootsIn(scope, '[data-aecis-tabs]').forEach((root) => {
      if (instances.has(root)) return;
      root.dataset.aecisReady = 'true';
      const tabs = [...root.querySelectorAll('[role="tab"]')];
      const cleanups = [];
      const select = (tab, focus = false) => {
        tabs.forEach((candidate) => {
          const active = candidate === tab;
          candidate.classList.toggle('is-active', active);
          candidate.setAttribute('aria-selected', String(active));
          candidate.tabIndex = active ? 0 : -1;
          const panel = root.querySelector(`#${CSS.escape(candidate.getAttribute('aria-controls'))}`);
          if (panel) panel.hidden = !active;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        const onClick = () => select(tab);
        const onKeydown = (event) => {
          let target = null;
          if (event.key === 'ArrowRight') target = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') target = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') target = tabs[0];
          if (event.key === 'End') target = tabs[tabs.length - 1];
          if (!target) return;
          event.preventDefault();
          select(target, true);
        };
        tab.addEventListener('click', onClick);
        tab.addEventListener('keydown', onKeydown);
        cleanups.push(() => tab.removeEventListener('click', onClick), () => tab.removeEventListener('keydown', onKeydown));
      });
      instances.set(root, () => {
        cleanups.forEach((cleanup) => cleanup());
        delete root.dataset.aecisReady;
      });
    });
  }

  function initPatches(scope) {
    rootsIn(scope, '[data-aecis-patches]').forEach((root) => {
      if (instances.has(root)) return;
      root.dataset.aecisReady = 'true';
      const viewport = root.querySelector('[data-aecis-patches-viewport]');
      const track = root.querySelector('[data-aecis-patches-track]');
      const slides = [...root.querySelectorAll('[data-aecis-patch-card]')];
      const previous = root.querySelector('[data-aecis-prev]');
      const next = root.querySelector('[data-aecis-next]');
      const pagination = root.querySelector('[data-aecis-pagination]');
      const cleanups = [];
      let position = 0;
      let pages = 1;
      let resizeFrame;
      let drag = null;
      let suppressClick = false;

      const perView = () => window.innerWidth < 576 ? 1 : window.innerWidth < 769 ? 2 : 4;
      const step = () => slides[0] ? slides[0].getBoundingClientRect().width + 24 : 0;
      const renderTransform = (offset = 0) => {
        track.style.transform = slides.length ? `translateX(${-position * step() + offset}px)` : '';
      };
      const createPagination = () => {
        if (pagination.children.length === pages) return;
        pagination.replaceChildren();
        for (let index = 0; index < pages; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', `Show patch slide ${index + 1} of ${pages}`);
          const onDotClick = () => { position = index; update(); };
          dot.addEventListener('click', onDotClick);
          pagination.append(dot);
        }
      };
      const update = () => {
        pages = Math.max(1, slides.length - perView() + 1);
        position = Math.max(0, Math.min(position, pages - 1));
        renderTransform();
        previous.disabled = position === 0;
        next.disabled = position >= pages - 1;
        createPagination();
        [...pagination.children].forEach((dot, index) => {
          dot.setAttribute('aria-label', `Show patch slide ${index + 1} of ${pages}`);
          if (index === position) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };
      const onPrevious = () => { position = Math.max(0, position - 1); update(); };
      const onNext = () => { position = Math.min(pages - 1, position + 1); update(); };
      const onResize = () => {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(update);
      };
      previous.addEventListener('click', onPrevious);
      next.addEventListener('click', onNext);
      window.addEventListener('resize', onResize);
      cleanups.push(
        () => previous.removeEventListener('click', onPrevious),
        () => next.removeEventListener('click', onNext),
        () => window.removeEventListener('resize', onResize),
        () => cancelAnimationFrame(resizeFrame)
      );

      const onPointerDown = (event) => {
        if (event.button !== 0 || event.target.closest?.('button,a,select,input,label')) return;
        drag = { id: event.pointerId, startX: event.clientX, deltaX: 0, moved: false };
        viewport.setPointerCapture?.(event.pointerId);
      };
      const onPointerMove = (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        drag.deltaX = event.clientX - drag.startX;
        if (Math.abs(drag.deltaX) > 8) drag.moved = true;
        if (!drag.moved) return;
        event.preventDefault();
        const boundedDelta = position === 0 && drag.deltaX > 0
          ? drag.deltaX * 0.25
          : position === pages - 1 && drag.deltaX < 0
            ? drag.deltaX * 0.25
            : drag.deltaX;
        renderTransform(boundedDelta);
      };
      const finishPointer = (event, cancelled = false) => {
        if (!drag || event.pointerId !== drag.id) return;
        viewport.releasePointerCapture?.(event.pointerId);
        if (!cancelled && drag.moved) {
          if (drag.deltaX < 0) position = Math.min(pages - 1, position + 1);
          if (drag.deltaX > 0) position = Math.max(0, position - 1);
          suppressClick = true;
        }
        drag = null;
        update();
      };
      const onPointerUp = (event) => finishPointer(event);
      const onPointerCancel = (event) => finishPointer(event, true);
      const onClickCapture = (event) => {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      };
      viewport.addEventListener('pointerdown', onPointerDown);
      viewport.addEventListener('pointermove', onPointerMove);
      viewport.addEventListener('pointerup', onPointerUp);
      viewport.addEventListener('pointercancel', onPointerCancel);
      viewport.addEventListener('click', onClickCapture, true);
      cleanups.push(
        () => viewport.removeEventListener('pointerdown', onPointerDown),
        () => viewport.removeEventListener('pointermove', onPointerMove),
        () => viewport.removeEventListener('pointerup', onPointerUp),
        () => viewport.removeEventListener('pointercancel', onPointerCancel),
        () => viewport.removeEventListener('click', onClickCapture, true)
      );

      slides.forEach((card) => {
        let variants = [];
        try { variants = JSON.parse(card.dataset.variants || '[]'); } catch (_) { variants = []; }
        const selector = card.querySelector('[data-aecis-variant-select]');
        const add = card.querySelector('[data-aecis-add]');
        const sold = card.querySelector('[data-aecis-sold-out]');
        const price = card.querySelector('[data-aecis-price]');
        const compare = card.querySelector('[data-aecis-compare]');
        const status = card.querySelector('[data-aecis-status]');
        const primary = card.querySelector('[data-aecis-primary]');
        const productPrimary = primary ? {
          src: primary.dataset.productPrimarySrc || primary.src,
          srcset: primary.dataset.productPrimarySrc && primary.dataset.productPrimarySrc !== primary.src ? '' : primary.srcset,
          alt: primary.dataset.productPrimaryAlt || primary.alt
        } : null;
        let selectedVariant = null;
        const setVariant = (id) => {
          const variant = variants.find((item) => String(item.id) === String(id));
          if (!variant) return;
          selectedVariant = variant;
          add.dataset.variantId = variant.id;
          add.hidden = !variant.available;
          add.disabled = !variant.available;
          sold.hidden = variant.available;
          price.textContent = money(variant.price);
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          compare.hidden = !onSale;
          compare.textContent = onSale ? money(variant.compare_at_price) : '';
          if (primary && productPrimary) {
            const featured = variant.featured_image;
            primary.src = featured?.src || productPrimary.src;
            primary.srcset = featured?.src ? '' : productPrimary.srcset;
            primary.alt = featured?.alt || productPrimary.alt;
          }
        };
        const onVariantChange = () => setVariant(selector.value);
        selector?.addEventListener('change', onVariantChange);
        if (selector) cleanups.push(() => selector.removeEventListener('change', onVariantChange));

        const onAdd = async () => {
          if (add.disabled || add.hidden) return;
          const variantId = Number(add.dataset.variantId);
          add.disabled = true;
          status.textContent = 'Adding to cart…';
          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: variantId, quantity: 1 }],
                sections: ['header-cart-status', 'cart-drawer'],
                sections_url: window.location.pathname
              })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.description || 'Unable to add this patch.');
            result.id ||= result.items?.[0]?.id || variantId;
            status.textContent = 'Added to cart.';
            const drawerForm = document.querySelector('sht-cart-drwr-frm');
            if (drawerForm && typeof drawerForm.renderContents === 'function' && result.sections?.['cart-drawer']) {
              drawerForm.renderContents(result);
            }
            const notification = document.querySelector('sht-cart-noti');
            if (notification && typeof notification.renderContents === 'function' && result.sections?.['header-cart-status']) {
              notification.renderContents(result, true, add, 1);
            }
            if (typeof window.upcartRefreshCart === 'function') await window.upcartRefreshCart();
            if (typeof window.upcartRegisterAddToCart === 'function') window.upcartRegisterAddToCart();
          } catch (error) {
            status.textContent = error.message || 'Unable to add this patch.';
            try {
              if (typeof window.upcartRefreshCart === 'function') await window.upcartRefreshCart();
            } catch (_) {}
          } finally {
            add.disabled = selectedVariant ? !selectedVariant.available : false;
          }
        };
        add.addEventListener('click', onAdd);
        cleanups.push(() => add.removeEventListener('click', onAdd));
        if (selector) setVariant(selector.value);
      });

      update();
      instances.set(root, () => {
        cleanups.forEach((cleanup) => cleanup());
        delete root.dataset.aecisReady;
      });
    });
  }

  function respectReducedMotion(scope) {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    rootsIn(scope, '.aecis-production__video').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause?.();
    });
  }

  function destroy(scope) {
    for (const selector of ['[data-aecis-tabs]', '[data-aecis-patches]']) {
      rootsIn(scope, selector).forEach((root) => {
        const cleanup = instances.get(root);
        if (!cleanup) return;
        cleanup();
        instances.delete(root);
      });
    }
  }

  const api = {
    init(scope = document) { initTabs(scope); initPatches(scope); respectReducedMotion(scope); },
    destroy
  };
  window.AecisInstantProductSections = api;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => api.init(document), { once: true });
  else api.init(document);
  document.addEventListener('shopify:section:load', (event) => api.init(event.target));
  document.addEventListener('shopify:section:unload', (event) => api.destroy(event.target));
})();
